import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { CREATION_SOURCE_LABEL, CreationSource } from 'services/VideoEditingService';
import { useBrollSearch } from './useBrollSearch';

// `broll_only` reads better as "broll only" in a chip.
export const humanize = (value: string) => value.replace(/_/g, ' ');

// Strip the `clip_subject:` style namespace so a tag filter reads like a word.
export const tagLabel = (tag: string) => {
  const colon = tag.indexOf(':');
  return colon === -1 ? tag : humanize(tag.slice(colon + 1));
};

/**
 * The library's filter controls, driven by useBrollSearch.
 *
 * Product leads on its own: narrowing further is only a meaningful choice once there is a set to
 * narrow, and every option below is a facet counted over THAT set — so offering them up front
 * would list terms from footage you aren't looking at.
 */
export default function BrollFilterBar({
  search, compact,
}: {
  search: ReturnType<typeof useBrollSearch>;
  // The picker modal has less room and no need for the source filter.
  compact?: boolean;
}) {
  const {
    facets, productCategory, setProductCategory, tags, setTags, categories, setCategories,
    creationSource, setCreationSource, query, setQuery, categoryName, activeFilters,
    clearFilters, loading, total,
  } = search;

  const countFor = (list: { value: string; count: number }[] | undefined, value: string) =>
    list?.find(f => f.value === value)?.count ?? 0;

  // The count belongs in the dropdown list, NOT in getOptionLabel: MUI writes the option label
  // into the input on selection, so "mattress topper (12)" would sit in the field, and typing to
  // change the choice would be filtering against text that includes the count — matching nothing.
  const optionText = (
    value: string, list: { value: string; count: number }[] | undefined, label = humanize(value),
  ) => `${label} (${countFor(list, value)})`;

  // Only offer tags that describe content — the product has its own dropdown, so repeating it
  // here would just be noise.
  const tagOptions = (facets?.tags || [])
    .filter(t => !t.value.startsWith('clip_product_category:') && !t.value.startsWith('clip_type:'))
    .map(t => t.value);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Autocomplete
          size="small"
          sx={{ width: 260 }}
          options={(facets?.productCategory || []).map(f => f.value)}
          getOptionLabel={humanize}
          renderOption={(props, v) => (
            <li {...props} key={v}>{optionText(v, facets?.productCategory)}</li>
          )}
          autoHighlight
          value={productCategory}
          onChange={(_, v) => setProductCategory(v)}
          renderInput={params => <TextField {...params} label="Product" autoFocus={!productCategory} />}
        />
        {!productCategory && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Narrow to one product, or search across all of them.
          </Typography>
        )}
      </Box>

      {/* Always offered. These used to appear only once a product was chosen, back when the
          product was a precondition for searching at all — with that gone, hiding them would
          leave the free-text box, the one filter that works best across products, unreachable
          from the state the page opens in. */}
      {(
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start', mt: 1.5 }}>
          <TextField
            size="small"
            sx={{ width: 260 }}
            placeholder="Search names, notes and keywords…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
          <Autocomplete
            size="small"
            multiple
            sx={{ width: 240 }}
            options={(facets?.categories || []).map(f => f.value)}
            getOptionLabel={categoryName}
            renderOption={(props, slug) => (
              <li {...props} key={slug}>{optionText(slug, facets?.categories, categoryName(slug))}</li>
            )}
            value={categories}
            onChange={(_, v) => setCategories(v)}
            renderTags={(value, getTagProps) => value.map((slug, i) => (
              <Chip size="small" label={categoryName(slug)} {...getTagProps({ index: i })} key={slug} />
            ))}
            renderInput={params => <TextField {...params} label="Categories" placeholder="Add a category" />}
          />
          <Autocomplete
            size="small"
            multiple
            sx={{ flexGrow: 1, minWidth: 240 }}
            options={tagOptions}
            getOptionLabel={tagLabel}
            renderOption={(props, t) => (
              <li {...props} key={t}>{optionText(t, facets?.tags, tagLabel(t))}</li>
            )}
            value={tags}
            onChange={(_, v) => setTags(v)}
            noOptionsText="Nothing else to narrow by in this product"
            renderTags={(value, getTagProps) => value.map((t, i) => (
              <Chip size="small" label={tagLabel(t)} {...getTagProps({ index: i })} key={t} />
            ))}
            renderInput={params => <TextField {...params} label="Shows / does" placeholder="Add a tag" />}
          />
          {!compact && (
            <Autocomplete
              size="small"
              sx={{ width: 180 }}
              options={(facets?.creationSource || []).map(f => f.value as CreationSource)}
              getOptionLabel={v => CREATION_SOURCE_LABEL[v] || v}
              renderOption={(props, v) => (
                <li {...props} key={v}>{optionText(v, facets?.creationSource, CREATION_SOURCE_LABEL[v] || v)}</li>
              )}
              value={creationSource}
              onChange={(_, v) => setCreationSource(v)}
              renderInput={params => <TextField {...params} label="Source" />}
            />
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {loading ? 'Searching…' : `${total} clip${total === 1 ? '' : 's'}`}
          {activeFilters > 0 && !loading ? ` · ${activeFilters} filter${activeFilters === 1 ? '' : 's'}` : ''}
        </Typography>
        {activeFilters > 0 && (
          <Button size="small" onClick={clearFilters}>Clear filters</Button>
        )}
      </Box>
    </>
  );
}
